import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { canReachCrmPage } from "components/_dashboard/contacts/ContactsPageSwitchButton";
import useFirestore from "hooks/useFirestore";
import { PATH_DASHBOARD } from "routes/paths";

/**
 * Which of the two "לקוחות" surfaces the user opened last — `/dashboard/contacts` (old)
 * or `/dashboard/crm` (new). Per-browser, like every other UI preference here; it is a
 * shortcut, not an entitlement (`canReachCrmPage` is what actually decides access).
 */
export const LAST_CONTACTS_SURFACE_STORAGE_KEY = "lastContactsSurface";

export type ContactsSurface = "old" | "new";

/**
 * The LIST surface a pathname is, or `null` — including for the standalone contact detail
 * (/dashboard/contacts/:id), which is deliberately not an answer here.
 *
 * That route is where a contact opened from the calendar, the calls table or the chat
 * lands; it is not a choice between the two lists. Counting it as "old" would silently
 * throw away a CRM user's preference every time they clicked a name somewhere else in the
 * dashboard. Neither list needs it: a row click on BOTH opens the detail IN PLACE via a
 * search param (`useOpenContactDetail`), leaving the list's own pathname alone.
 *
 * `end: false` on the CRM path keeps its org-settings child (/dashboard/crm/settings) —
 * reachable only from the CRM page itself — on the new surface.
 */
const listSurfaceOfPathname = (pathname: string): ContactsSurface | null => {
  if (matchPath({ path: PATH_DASHBOARD.general.crm, end: false }, pathname)) return "new";
  if (matchPath({ path: PATH_DASHBOARD.general.contacts, end: true }, pathname)) return "old";
  return null;
};

/**
 * Where the sidebar's "לקוחות" entry points — the surface the user actually uses.
 *
 * There is ONE entry for two pages (see `LocalizedSidebarConfig`), so a user who has moved
 * to the new CRM page would otherwise be sent back to the old one by their own sidebar on
 * every click, and would have to re-cross via `ContactsPageSwitchButton` each time. The
 * entry follows them instead: open one, and that is the one the link means.
 *
 * Remembering is derived from the URL rather than written by the two pages, so there is one
 * place to keep correct and no page can forget to report itself.
 *
 * The stored value is a HINT, never a grant: it is only honoured while `canReachCrmPage`
 * still holds — the same rule the switch button gates itself on.
 */
export default function useContactsSidebarPath(): string {
  const { pathname } = useLocation();
  const { userSettings } = useFirestore();
  const [lastSurface, setLastSurface] = useLocalStorage<ContactsSurface>(
    LAST_CONTACTS_SURFACE_STORAGE_KEY,
    "old"
  );

  const surfaceHere = listSurfaceOfPathname(pathname);

  useEffect(() => {
    if (surfaceHere && surfaceHere !== lastSurface) setLastSurface(surfaceHere);
  }, [surfaceHere, lastSurface, setLastSurface]);

  // `surfaceHere` first, so the entry is already correct on the render that lands on a
  // page — waiting for the effect would leave the sidebar pointing away from the page the
  // user is standing on (and, since NavSection matches the entry's own path, unhighlighted).
  const surface = surfaceHere ?? lastSurface;

  return surface === "new" && canReachCrmPage(userSettings)
    ? PATH_DASHBOARD.general.crm
    : PATH_DASHBOARD.general.contacts;
}
